import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDatePicker } from "go-form/components/FormDatePicker";
import { FormDirty } from "go-form/components/FormDirty";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { apiOrganization } from "../../../../../../../../../services/Api/Organization/apiOrganization";
import { CategoryApi, ItemApi, ItemSuspensionApi } from "../../../../../../../../../services/Api/Organization/types";
import BlockProductOrderSourcesCard from "../../../../../components/Order/BlockProduct/BlockProductOrderSourcesCard";
import BlockProductOrderTypesCard from "../../../../../components/Order/BlockProduct/BlockProductOrderTypesCard";

interface Props {
	isShown: boolean;
	onHide: () => void;
	item: ItemApi | CategoryApi;
	handleSuccessAction: (item: ItemApi | CategoryApi) => Promise<void>;
	editMode?: boolean;
	setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
}

const LiveOrdersBlockItemModal = ({
	isShown,
	onHide,
	item,
	handleSuccessAction,
	editMode,
	setEditMode,
}: Props): JSX.Element => {
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const [loading, setLoading] = useState(false);
	const form = useForm<ItemSuspensionApi>({
		criteriaMode: "all",
		defaultValues: {
			order_types: item.suspension?.order_types || [],
			sources: item.suspension?.sources || [],
			reason: item.suspension?.reason && item.suspension.reason !== "-" ? item.suspension.reason : "",
			until: item.suspension?.until,
		},
	});
	const {
		formState,
		formState: { errors },
		handleSubmit,
		setError,
		control,
		register,
		setValue,
		watch,
	} = form;
	const confirmation = useConfirmation();
	const [blockItemLoading, setBlockItemLoading] = useState(false);

	useEffect(() => {
		return () => {
			setEditMode(false);
		};
	});

	const handleOnHide = () => {
		if (editMode) {
			setEditMode(false);
		}
		onHide();
	};

	const onSubmit = handleSubmit(async (data: ItemSuspensionApi) => {
		setLoading(true);
		try {
			let updatedItem: ItemApi | CategoryApi;

			if ("category" in item) {
				updatedItem = await apiOrganization.blockItem(item.id, data, {
					include: "category,item_group",
				});
			} else {
				updatedItem = await apiOrganization.blockCategory(item.id, data);
			}
			await handleSuccessAction(updatedItem);
			handleOnHide();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const handleUnblock = async () => {
		try {
			await confirmation({
				title: t("lib:confirmation.title"),
				message: t("lib:confirmation.message.action"),
			});
			setBlockItemLoading(true);
			let res: ItemApi | CategoryApi;

			if ("category" in item) {
				res = await apiOrganization.unblockItem(item.id, {
					include: "category,item_group",
				});
			} else {
				res = await apiOrganization.unblockCategory(item.id);
			}
			await handleSuccessAction(res);
			setEditMode(false);
			handleOnHide();
			addSuccessFlash(t("lib:common.flash.completed"));
		} catch (err) {
			handleError.alert(err, addFlash);
		}
		setBlockItemLoading(false);
	};

	return (
		<Modal show={isShown} onHide={handleOnHide}>
			<FormDirty formState={formState}>
				<Modal.Header closeButton>
					<Modal.Title>{t("modules.live_order.field.block_product.title")}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<FormDatePicker
						errors={errors}
						control={control}
						name="until"
						label={t("modules.live_order.field.until.title")}
						placeholder={t("modules.live_order.field.until.placeholder")}
					/>
					<FormInput
						name="reason"
						errors={errors}
						register={register}
						label={t("modules.live_order.field.reason.title")}
						as="textarea"
					/>
					<BlockProductOrderTypesCard
						handleSetOrderTypes={(orderTypes) => setValue("order_types", orderTypes)}
						watchedOrderTypes={watch("order_types")}
					/>
					<BlockProductOrderSourcesCard
						handleSetSources={(sources) => setValue("sources", sources)}
						watchedSources={watch("sources")}
					/>
				</Modal.Body>
				<Modal.Footer>
					{editMode ? (
						<>
							<ButtonLoading loading={loading} type="submit" onClick={onSubmit}>
								{t("modules.live_order.action.save_changes.title")}
							</ButtonLoading>
							<ButtonLoading loading={blockItemLoading} variant="success" onClick={handleUnblock}>
								{t("modules.live_order.action.unblock.title")}
							</ButtonLoading>
						</>
					) : (
						<ButtonLoading loading={loading} variant="danger" type="submit" onClick={onSubmit}>
							{t("modules.live_order.action.block.title")}
						</ButtonLoading>
					)}
					<Button variant="light" onClick={onHide}>
						{t("lib:common.action.cancel")}
					</Button>
				</Modal.Footer>
			</FormDirty>
		</Modal>
	);
};

export default LiveOrdersBlockItemModal;
